import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Modal, Spinner, Alert, Tooltip, OverlayTrigger, ProgressBar, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { MODE_REGLEMENT } from "../utils/constantes";
import './Factures.css';
import { API_BASE_URL } from "../constants/api";

export default function FacturesImpayees() {
    const navigate = useNavigate();
    const [factures, setFactures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [showPaiementModal, setShowPaiementModal] = useState(false);
    const [factureSelectionnee, setFactureSelectionnee] = useState(null);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'date_echeance', direction: 'asc' });
    
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    
    const [paiement, setPaiement] = useState({
        montant: '',
        mode_reglement: 'especes',
        reference: '',
        date_reglement: new Date().toISOString().split('T')[0]
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchImpayees();
        fetchStats();
    }, []);

    const fetchImpayees = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?action=impayees`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setFactures(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const handlePaiementSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            
            if (!user || !user.id) {
                showError("Utilisateur non identifié");
                setSaving(false);
                return;
            }

            const response = await fetch(
                `${API_BASE_URL}/factures.php`,
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'encaisser',
                        id_facture: factureSelectionnee.id_facture,
                        montant: parseFloat(paiement.montant),
                        mode_reglement: paiement.mode_reglement,
                        reference: paiement.reference,
                        id_utilisateur: user.id
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowPaiementModal(false);
                fetchImpayees();
                fetchStats();
                showSuccess("Paiement enregistré avec succès");
                setPaiement({
                    montant: '',
                    mode_reglement: 'especes',
                    reference: '',
                    date_reglement: new Date().toISOString().split('T')[0]
                });
            } else {
                showError(result.message || "Erreur lors du paiement");
            }
        } catch (err) {
            console.error("Erreur paiement:", err);
            showError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const getJoursRetard = (dateEcheance) => {
        const today = new Date();
        const echeance = new Date(dateEcheance);
        const diffTime = today - echeance;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const getResteCouleur = (reste, joursRetard) => {
        if (reste === 0) return 'text-success';
        if (joursRetard > 7) return 'text-danger fw-bold';
        if (joursRetard > 0) return 'text-danger';
        return 'text-warning';
    };

    const filteredFactures = useMemo(() => {
        return factures
            .filter(f => 
                f.numero_facture?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.code_client?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                const aRetard = getJoursRetard(a.date_echeance) > 0;
                const bRetard = getJoursRetard(b.date_echeance) > 0;
                
                if (aRetard && !bRetard) return -1;
                if (!aRetard && bRetard) return 1;
                
                if (sortConfig.key === 'date_echeance') {
                    return sortConfig.direction === 'asc'
                        ? getJoursRetard(a.date_echeance) - getJoursRetard(b.date_echeance)
                        : getJoursRetard(b.date_echeance) - getJoursRetard(a.date_echeance);
                }
                if (sortConfig.key === 'montant') {
                    const resteA = a.montant_ttc - (a.montant_regle || 0);
                    const resteB = b.montant_ttc - (b.montant_regle || 0);
                    return sortConfig.direction === 'asc' ? resteA - resteB : resteB - resteA;
                }
                return 0;
            });
    }, [factures, filterText, sortConfig]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredFactures.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredFactures.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
            if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>);
        }

        return items;
    };

    const totalImpaye = currentItems.reduce((acc, f) => acc + (f.montant_ttc - (f.montant_regle || 0)), 0);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th onClick={() => handleSort(field)} style={{ cursor: 'pointer', userSelect: 'none' }} className="sortable-header">
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>}
            </div>
        </th>
    );

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}>
            <Button variant={variant} size="sm" onClick={onClick} disabled={disabled} className="action-btn">
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading && !factures.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="loading-animation mb-4">
                                <div className="loading-ring"><div className="loading-ring-inner"></div></div>
                                <i className="ri-error-warning-line loading-icon"></i>
                            </div>
                            <h5 className="fw-bold text-primary mb-2">Chargement des factures impayées</h5>
                            <p className="text-muted small mb-0">Analyse des créances en cours...</p>
                            <div className="mx-auto mt-4" style={{ width: '200px' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef' }}>
                                    <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%', background: 'linear-gradient(90deg, #4361ee, #10b981, #f59e0b)' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-animation { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; background: linear-gradient(135deg, #21ACCD, #10b981); animation: pulse-ring 1.5s ease-in-out infinite; }
                    .loading-ring-inner { position: absolute; top: 5px; left: 5px; right: 5px; bottom: 5px; border-radius: 50%; background: white; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: #21ACCD; z-index: 2; animation: pulse-icon 1.5s ease-in-out infinite; }
                    @keyframes pulse-ring { 0% { transform: scale(0.9); opacity: 0.7; } 50% { transform: scale(1.1); opacity: 0.3; } 100% { transform: scale(0.9); opacity: 0.7; } }
                    @keyframes pulse-icon { 0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; } }
                `}</style>
            </>
        );
    }

    if (error && !factures.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchImpayees}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    
                    {/* Alerts */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 rounded-3 border-0 shadow-sm">
                            <div className="d-flex align-items-center">
                                <i className="ri-checkbox-circle-line fs-4 me-3 text-success"></i>
                                <div>{successMessage}</div>
                            </div>
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 rounded-3 border-0 shadow-sm">
                            <div className="d-flex align-items-center">
                                <i className="ri-error-warning-line fs-4 me-3 text-danger"></i>
                                <div>{errorMessage}</div>
                            </div>
                        </Alert>
                    )}

                    {/* En-tête moderne */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-danger bg-opacity-10 rounded-3">
                                    <i className="ri-error-warning-line fs-20 text-danger"></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Factures impayées</h4>
                                    <p className="text-muted small mb-0">Gérez les paiements en retard et les impayés</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-primary" 
                                onClick={() => navigate('/ventes/factures')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Toutes les factures
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-danger">
                                <div className="kpi-icon"><i className="ri-error-warning-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total impayés</span>
                                    <h3 className="kpi-value">{formatCurrency(totalImpaye)}</h3>
                                    <span className="kpi-unit">{filteredFactures.length} facture(s)</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon"><i className="ri-timer-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Retard maximum</span>
                                    <h3 className="kpi-value">
                                        {currentItems.length > 0 
                                            ? Math.max(...currentItems.map(f => { const jours = getJoursRetard(f.date_echeance); return jours > 0 ? jours : 0; }))
                                            : 0} <span className="fs-10">jours</span>
                                    </h3>
                                    <span className="kpi-unit">À traiter urgemment</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon"><i className="ri-group-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Clients concernés</span>
                                    <h3 className="kpi-value">{new Set(factures.map(f => f.id_client)).size}</h3>
                                    <span className="kpi-unit">clients débiteurs</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon"><i className="ri-bar-chart-2-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Moyenne par facture</span>
                                    <h3 className="kpi-value">
                                        {filteredFactures.length > 0 
                                            ? formatCurrency(totalImpaye / filteredFactures.length)
                                            : formatCurrency(0)}
                                    </h3>
                                    <span className="kpi-unit">montant moyen</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Filtres */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="8">
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher facture, client, code..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md="4">
                                    <div className="text-end">
                                        <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                            <i className="ri-stack-line me-1"></i>
                                            {filteredFactures.length} facture(s) impayée(s)
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tableau des factures impayées */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">N° Facture</th>
                                            <th>Date</th>
                                            <th>Client</th>
                                            <SortableHeader field="date_echeance">Échéance</SortableHeader>
                                            <th className="text-end">Montant</th>
                                            <th className="text-end">Réglé</th>
                                            <SortableHeader field="montant" className="text-end">Reste</SortableHeader>
                                            <th>Progression</th>
                                            <th>Retard</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? currentItems.map((facture) => {
                                            const reste = facture.montant_ttc - (facture.montant_regle || 0);
                                            const joursRetard = getJoursRetard(facture.date_echeance);
                                            const estEnRetard = joursRetard > 0;
                                            const progression = (facture.montant_regle / facture.montant_ttc) * 100;
                                            
                                            return (
                                                <tr key={facture.id_facture} className={estEnRetard ? 'table-danger' : ''}>
                                                    <td className="ps-4 fw-semibold">
                                                        <Link to={`/ventes/facturesDetails/${facture.id_facture}`} className="table-link">
                                                            {facture.numero_facture}
                                                        </Link>
                                                    </td>
                                                    <td className="text-muted">{formatDate(facture.date_facture)}</td>
                                                    <td>
                                                        <div className="fw-semibold">{facture.client_nom}</div>
                                                        <small className="text-muted">{facture.code_client}</small>
                                                    </td>
                                                    <td>
                                                        <div className="fw-semibold">{formatDate(facture.date_echeance)}</div>
                                                        {estEnRetard && <small className="text-danger">En retard</small>}
                                                    </td>
                                                    <td className="text-end fw-bold">{formatCurrency(facture.montant_ttc)}</td>
                                                    <td className="text-end text-success">{formatCurrency(facture.montant_regle || 0)}</td>
                                                    <td className={`text-end ${getResteCouleur(reste, joursRetard)}`}>{formatCurrency(reste)}</td>
                                                    <td style={{ minWidth: '100px' }}>
                                                        <div className="d-flex flex-column">
                                                            <ProgressBar now={progression} variant={progression === 100 ? 'success' : 'warning'} className="rounded-pill" style={{ height: '6px' }} />
                                                            <small className="text-muted mt-1">{Math.round(progression)}% payé</small>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`status-badge ${estEnRetard ? 'status-danger' : 'status-success'}`}>
                                                            <i className={`${estEnRetard ? 'ri-alarm-warning-line' : 'ri-check-line'} me-1 fs-10`}></i>
                                                            {estEnRetard ? `J+${joursRetard}` : `J-${Math.abs(joursRetard)}`}
                                                        </span>
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <Link to={`/ventes/facturesDetails/${facture.id_facture}`} className="action-icon" title="Voir détails">
                                                                <i className="ri-eye-line"></i>
                                                            </Link>
                                                            <button 
                                                                className="action-icon action-icon-success" 
                                                                onClick={() => {
                                                                    setFactureSelectionnee(facture);
                                                                    setPaiement({...paiement, montant: reste});
                                                                    setShowPaiementModal(true);
                                                                }}
                                                                title="Encaisser"
                                                            >
                                                                <i className="ri-bank-card-line"></i>
                                                            </button>
                                                            <Link to={`/ventes/facturesDetails/${facture.id_facture}`} className="action-icon action-icon-warning" title="Créer un avoir">
                                                                <i className="ri-file-copy-line"></i>
                                                            </Link>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan="10" className="text-center py-5">
                                                    <i className="ri-checkbox-circle-line fs-48 text-success mb-3 d-block"></i>
                                                    <h6 className="mb-1">Aucune facture impayée</h6>
                                                    <p className="text-muted small mb-0">Toutes les factures sont soldées</p>
                                                    {filterText && (
                                                        <Button variant="outline-primary" size="sm" className="mt-3 rounded-pill" onClick={() => setFilterText('')}>
                                                            <i className="ri-refresh-line me-1"></i>Réinitialiser
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && !loading && (
                                <div className="d-flex justify-content-between align-items-center mt-4 px-3">
                                    <div className="text-muted small">
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredFactures.length)} sur {filteredFactures.length} facture(s)
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* Modal de paiement */}
            <Modal show={showPaiementModal} onHide={() => setShowPaiementModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pt-4 px-4">
                    <div className="d-flex align-items-center gap-2">
                        <div className="p-2 rounded-3 bg-success bg-opacity-10"><i className="ri-bank-card-line fs-20 text-success"></i></div>
                        <Modal.Title className="fs-5 fw-bold">Enregistrer un paiement</Modal.Title>
                    </div>
                </Modal.Header>
                <Form onSubmit={handlePaiementSubmit}>
                    <Modal.Body className="px-4 pb-4">
                        {factureSelectionnee && (
                            <>
                                <Alert variant="light" className="mb-4 p-3 rounded-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong>{factureSelectionnee.numero_facture}</strong>
                                            <div className="small text-muted">{factureSelectionnee.client_nom}</div>
                                        </div>
                                        <Badge bg="danger" className="px-3 py-2 rounded-pill">
                                            Reste: {formatCurrency(factureSelectionnee.montant_ttc - (factureSelectionnee.montant_regle || 0))}
                                        </Badge>
                                    </div>
                                </Alert>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold small">Date de paiement</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={paiement.date_reglement}
                                                onChange={(e) => setPaiement({...paiement, date_reglement: e.target.value})}
                                                required
                                                className="border-2 rounded-3"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold small">Montant</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    min="0.01"
                                                    max={factureSelectionnee.montant_ttc - (factureSelectionnee.montant_regle || 0)}
                                                    value={paiement.montant}
                                                    onChange={(e) => setPaiement({...paiement, montant: e.target.value})}
                                                    required
                                                    className="border-2 rounded-3"
                                                />
                                                <InputGroup.Text className="bg-light border-2">FCFA</InputGroup.Text>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold small">Mode de règlement</Form.Label>
                                    <Form.Select
                                        value={paiement.mode_reglement}
                                        onChange={(e) => setPaiement({...paiement, mode_reglement: e.target.value})}
                                        required
                                        className="border-2 rounded-3"
                                    >
                                        {MODE_REGLEMENT.map(mode => (
                                            <option key={mode.value} value={mode.value}>{mode.icon} {mode.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold small">Référence (optionnel)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="N° chèque, référence virement..."
                                        value={paiement.reference}
                                        onChange={(e) => setPaiement({...paiement, reference: e.target.value})}
                                        className="border-2 rounded-3"
                                    />
                                </Form.Group>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 px-4">
                        <Button variant="light" onClick={() => setShowPaiementModal(false)} className="rounded-pill px-4">Annuler</Button>
                        <Button variant="success" type="submit" disabled={saving} className="rounded-pill px-4" style={{ background: '#21ACCD', border: 'none' }}>
                            {saving ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : <><i className="ri-check-line me-2"></i>Enregistrer</>}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <style>{`
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-danger { border-left-color: #dc3545; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-info { border-left-color: #0dcaf0; }
                .kpi-primary { border-left-color: #21ACCD; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #dc3545; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0dcaf0; }
                .kpi-primary .kpi-icon { background: #e8f0fe; color: #21ACCD; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }
                .status-danger { background: #fef2f2; color: #dc3545; }
                .status-success { background: #ecfdf5; color: #10b981; }
                
                .action-icon {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    color: #6c757d;
                    transition: all 0.2s;
                    cursor: pointer;
                }
                .action-icon:hover {
                    background: #21ACCD;
                    border-color: #21ACCD;
                    color: white;
                }
                .action-icon-success:hover {
                    background: #198754;
                    border-color: #198754;
                    color: white;
                }
                .action-icon-warning:hover {
                    background: #f59e0b;
                    border-color: #f59e0b;
                    color: white;
                }
                
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                .fs-10 {
                    font-size: 0.625rem;
                }
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                .modern-modal .modal-content {
                    border: none;
                    border-radius: 24px;
                    overflow: hidden;
                }
            `}</style>
        </React.Fragment>
    );
}